import React, {useEffect} from 'react';
import {connect} from "react-redux";
import {Link} from "react-router-dom";
import {withTranslation} from "react-i18next";
import Footer from "../core/layout/footer/footer";
import './softwareDevelopmentServices.css';

/* ─── Section data ───────────────────────────── */
const aiCards = [
    {icon: 'fa-brain',    title: 'Ride the AI Wave',                 desc: 'Leverage custom solutions that combine large language models like OpenAI with your business data — for smarter products, faster decisions, and better user experiences.'},
    {icon: 'fa-comments', title: 'Immersive Conversational Abilities', desc: 'Integrate ChatGPT-like conversational abilities seamlessly into your operations — for support, onboarding, internal tools, or customer-facing products.'},
];

const reasons = [
    {delay: '',   title: 'Escape Cookie-Cutter Solutions', desc: 'Tired of generic solutions? Custom web application development aligns precisely with your unique business processes and goals.'},
    {delay: 'd1', title: 'Soar Above the Competition',     desc: 'Achieve tailored customer experiences that outshine competitors and make lasting impressions — built around what your users actually need.'},
    {delay: 'd2', title: 'Master Your Data',               desc: 'With custom development, take full control of your data — ensuring the privacy, security, and governance that off-the-shelf tools can\'t provide.'},
    {delay: 'd3', title: 'Freedom from Limitations',       desc: 'Customize every aspect without platform constraints. Embrace innovation on your terms and watch your solution evolve with your business.'},
    {delay: 'd4', title: 'Investing in the Future',        desc: 'Custom web application development is a long-term investment, not just an expense. It demands patience but delivers lasting value and competitive moat.'},
];

const webExpertise = [
    {icon: 'fa-mobile-alt',        delay: '',   title: 'Device-Friendly Design',    desc: 'Responsive designs for a seamless experience across any device — mobile, tablet, laptop, or desktop.'},
    {icon: 'fa-rocket',            delay: 'd1', title: 'Rapid Feature Deployment',  desc: 'Outpace competitors with swift feature unveiling. Our agile process keeps your product moving faster than the market.'},
    {icon: 'fa-paint-brush',       delay: 'd2', title: 'Flawless UX',               desc: 'Expertly curated UI-UX design for a perfect customer journey — from first impression to repeated use.'},
    {icon: 'fa-expand-arrows-alt', delay: 'd3', title: 'Flexible Scaling',          desc: 'Effortless scalability from single-machine to microservices clusters. Expand at your own pace without re-architecting.'},
];

const mobileExpertise = [
    {icon: 'fa-layer-group', delay: '',   title: 'Cross-Platform Expertise',   desc: 'Deep expertise in both iOS and Android ecosystems, tackling common challenges head-on with proven frameworks.'},
    {icon: 'fa-bolt',        delay: 'd1', title: 'Rapid Time-to-Market',       desc: 'Accelerate time-to-market with our pre-built frameworks for push notifications, in-app messages, social interactions, and much more.'},
    {icon: 'fa-route',       delay: 'd2', title: 'Seamless Customer Journeys', desc: 'Craft a flawless customer journey through unified UI-UX experiences, curated by our expert design and engineering team.'},
    {icon: 'fa-shield-alt',  delay: 'd3', title: 'App Stability',              desc: 'Robust crash detection and ANR prevention framework for enhanced app stability — so your users never see a bad experience.'},
];

const staffRoles = [
    {icon: 'fa-code',      delay: '',   adj: 'Top-notch',  title: 'Developers'},
    {icon: 'fa-paint-brush', delay: 'd1', adj: 'Creative',   title: 'UI-UX Designers'},
    {icon: 'fa-chart-bar', delay: 'd2', adj: 'Insightful',  title: 'Business Analysts'},
    {icon: 'fa-bug',       delay: 'd3', adj: 'Meticulous',  title: 'Testers'},
];

/* ─── Component ──────────────────────────────── */
function SoftwareDevelopmentServices() {

    useEffect(() => {
        window.scrollTo(0, 0);

        const io = new IntersectionObserver(entries => {
            entries.forEach(e => {
                if (e.isIntersecting) {
                    e.target.classList.add('visible');
                    io.unobserve(e.target);
                }
            });
        }, {threshold: 0.08});

        document.querySelectorAll('.csd-fade-up').forEach(el => io.observe(el));
        return () => io.disconnect();
    }, []);

    /* ── Section 1: Hero ─────────────────────── */
    const renderHero = () => (
        <section className="csd-page-hero">
            <div className="csd-page-hero-inner">
                <div className="csd-page-eyebrow">
                    <i className="fas fa-laptop-code"></i> Custom Software Development
                </div>
                <p className="csd-hero-sub">The Future is Now</p>
                <h1>AI-Driven Custom Solutions <span>for Your Business</span></h1>
                <p className="csd-hero-desc">
                    Ride the AI wave and unlock the full potential of your business with our custom
                    solutions. The current state of AI enables you to implement ideas like never before.
                </p>
            </div>
        </section>
    );

    /* ── Section 2: AI-Driven Solutions ─────── */
    const renderAiSection = () => (
        <section className="csd-content">
            <div className="csd-wrap">
                <div className="csd-ai-layout">
                    <div className="csd-ai-left csd-fade-up">
                        <div className="csd-section-tag">AI-Powered Development</div>
                        <h2>Leverage AI in <span>everything we build</span></h2>
                        <p>
                            We don't treat AI as an add-on. Every custom solution we build considers where AI
                            can replace manual effort, accelerate workflows, and create differentiated experiences
                            for your users.
                        </p>
                        <p>
                            From LLM-powered features to intelligent automation — we bring the latest AI
                            capabilities into practical, production-ready software.
                        </p>
                    </div>
                    <div className="csd-ai-cards csd-fade-up d1">
                        {aiCards.map(({icon, title, desc}) => (
                            <div key={title} className="csd-ai-mini-card">
                                <div className="csd-ai-mini-icon"><i className={`fas ${icon}`}></i></div>
                                <div>
                                    <h4>{title}</h4>
                                    <p>{desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );

    /* ── Section 3: Why Custom ───────────────── */
    const renderWhyCustom = () => (
        <section className="csd-content csd-alt-bg">
            <div className="csd-wrap">
                <div className="csd-fade-up">
                    <div className="csd-section-tag">Why Custom?</div>
                    <h2 className="csd-s-title">Why custom web and <span>mobile applications?</span></h2>
                    <p className="csd-s-sub">
                        Off-the-shelf solutions come with trade-offs that compound over time. Custom development
                        is an investment that pays back through ownership, flexibility, and fit.
                    </p>
                </div>
                <div className="csd-reasons-grid">
                    {reasons.map(({delay, title, desc}) => (
                        <div key={title} className={`csd-reason-card csd-fade-up${delay ? ' ' + delay : ''}`}>
                            <h3>{title}</h3>
                            <p>{desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );

    /* ── Section 4: Web Expertise ───────────── */
    const renderWebExpertise = () => (
        <section className="csd-content">
            <div className="csd-wrap">
                <div className="csd-fade-up">
                    <div className="csd-section-tag">Web Applications</div>
                    <h2 className="csd-s-title">Our expertise in <span>web application development</span></h2>
                    <p className="csd-s-sub">
                        We design and build web applications that perform, scale, and delight — across every
                        device and use case.
                    </p>
                </div>
                <div className="csd-expertise-grid">
                    {webExpertise.map(({icon, delay, title, desc}) => (
                        <div key={title} className={`csd-exp-card csd-fade-up${delay ? ' ' + delay : ''}`}>
                            <div className="csd-exp-icon"><i className={`fas ${icon}`}></i></div>
                            <div>
                                <h3>{title}</h3>
                                <p>{desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );

    /* ── Section 5: Mobile Expertise ────────── */
    const renderMobileExpertise = () => (
        <section className="csd-content csd-alt-bg">
            <div className="csd-wrap">
                <div className="csd-fade-up">
                    <div className="csd-section-tag">Mobile Applications</div>
                    <h2 className="csd-s-title">Our expertise in <span>mobile application development</span></h2>
                    <p className="csd-s-sub">
                        Native and cross-platform mobile apps that are fast, stable, and built for real users
                        across iOS and Android.
                    </p>
                </div>
                <div className="csd-expertise-grid">
                    {mobileExpertise.map(({icon, delay, title, desc}) => (
                        <div key={title} className={`csd-exp-card csd-fade-up${delay ? ' ' + delay : ''}`}>
                            <div className="csd-exp-icon"><i className={`fas ${icon}`}></i></div>
                            <div>
                                <h3>{title}</h3>
                                <p>{desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );

    /* ── Section 6: Staff Augmentation ──────── */
    const renderStaffAug = () => (
        <section className="csd-content">
            <div className="csd-wrap">
                <div className="csd-fade-up">
                    <div className="csd-section-tag">Staff Augmentation</div>
                    <h2 className="csd-s-title">Elevate your team with <span>expert staff augmentation</span></h2>
                    <p className="csd-s-sub">
                        Elevate your team's capabilities with our comprehensive staff augmentation services.
                        Whether you need to fill a skills gap or scale a project team, we bring the right people.
                    </p>
                </div>
                <div className="csd-staff-grid">
                    {staffRoles.map(({icon, delay, adj, title}) => (
                        <div key={title} className={`csd-staff-card csd-fade-up${delay ? ' ' + delay : ''}`}>
                            <div className="csd-staff-icon"><i className={`fas ${icon}`}></i></div>
                            <div className="csd-staff-adj">{adj}</div>
                            <h3>{title}</h3>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );

    /* ── Section 7: CTA Banner ───────────────── */
    const renderCTA = () => (
        <section className="csd-cta-banner">
            <div className="csd-cta-inner">
                <div className="csd-fade-up">
                    <h2>Ready to build something custom?</h2>
                    <p>
                        Tell us about your idea or challenge. We'll be direct about what it takes — and how
                        we'd approach it together.
                    </p>
                </div>
                <div className="csd-cta-actions csd-fade-up d2">
                    <Link to="/contact-us" className="dl-btn dl-btn-white">
                        <i className="fas fa-envelope"></i> info@deccanlogic.com
                    </Link>
                </div>
            </div>
        </section>
    );

    /* ── Render ──────────────────────────────── */
    return (
        <div className="w-100">
            {renderHero()}
            {renderAiSection()}
            {renderWhyCustom()}
            {renderWebExpertise()}
            {renderMobileExpertise()}
            {renderStaffAug()}
            {renderCTA()}
            <Footer/>
        </div>
    );
}

SoftwareDevelopmentServices.propTypes = {};

SoftwareDevelopmentServices.defaultProps = {};

const mapStateToProps = (state) => ({
    isMobileView: state.appState.deviceInfo.isMobileView,
    isLGView: state.appState.deviceInfo.isLGView,
    isXLView: state.appState.deviceInfo.isXLView,
    isXSView: state.appState.deviceInfo.isXSView,
    isSMView: state.appState.deviceInfo.isSMView,
});

export default connect(mapStateToProps, {})(withTranslation('translations')(SoftwareDevelopmentServices));
